import { Grid2, Stack, Typography } from '@mui/material';
import { Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { array, object, string } from 'yup';

import { FormikCodeField, FormikTelField } from '@components/FormikFields';
import RoundedButton from '@components/RoundedButton/index';

import {
  useAdminChangePhone,
  useChangeUserPhone,
  useVerifyPhone,
} from '@hooks/api/session';
import { useGetUserMfaSettings } from '@hooks/api/user';
import useCommonSchemas from '@hooks/schemas/useCommonSchemas';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

const messages = defineMessages({
  currentPhone: {
    id: 'changePhone.currentPhone',
    defaultMessage: 'Current Phone:',
  },
  noPhone: {
    id: 'changePhone.noPhone',
    defaultMessage: 'No phone number configured',
  },
  verified: {
    id: 'changePhone.verified',
    defaultMessage: 'Verified',
  },
  notVerified: {
    id: 'changePhone.notVerified',
    defaultMessage: 'Not Verified',
  },
  newPhone: {
    id: 'changePhone.newPhone',
    defaultMessage: 'New Phone Number',
  },
  submit: {
    id: 'changePhone.submit',
    defaultMessage: 'Update Phone',
  },
  verificationCode: {
    id: 'changePhone.verificationCode',
    defaultMessage: 'Verification Code',
  },
  verify: {
    id: 'changePhone.verify',
    defaultMessage: 'Verify',
  },
  disclaimer: {
    id: 'changePhone.disclaimer',
    defaultMessage:
      'Your phone number could be used as an account recovery option',
  },
  codeSent: {
    id: 'changePhone.codeSent',
    defaultMessage:
      'A verification code has been sent to your new phone number.',
  },
});

const CODE_LENGTH = 6;

type FormValues = {
  phone: string;
  verificationCode: string[];
};

interface ChangePhoneProps {
  onVerified?: (phoneNumber: string) => void;
}

export default function ChangePhone({ onVerified }: ChangePhoneProps) {
  const { formatMessage } = useIntl();
  const { phoneSchema } = useCommonSchemas();
  const { enqueueSnackbar } = useSnackbar();
  const [changeUserPhone] = useChangeUserPhone();
  const [adminChangePhone] = useAdminChangePhone();
  const [verifyUserPhone, verifyLoading] = useVerifyPhone();
  const { data: mfaPreference, refetch } = useGetUserMfaSettings();
  const mfaResponse = mfaPreference?.getUserRecord.mfa;
  const {
    state: { activeProfile, loggedInProfile },
  } = useSession();
  const isEditingSelf =
    !!activeProfile?.user_id &&
    activeProfile.user_id === loggedInProfile?.user_id;

  const [codeSent, setCodeSent] = React.useState(false);

  const validationSchema = object().shape({
    phone: phoneSchema.required(),
    verificationCode: codeSent
      ? array()
          .of(string().required())
          .length(CODE_LENGTH, 'Verification code is required')
      : array().of(string()),
  });

  const initialValues: FormValues = {
    phone: '',
    verificationCode: Array(CODE_LENGTH).fill(''),
  };

  async function handleSubmitPhone(values: FormValues) {
    if (!isEditingSelf) {
      if (!activeProfile?.user_id) {
        throw new Error('No active profile selected');
      }
      const { data } = await adminChangePhone({
        input: {
          user_id: activeProfile.user_id,
          newPhone: values.phone,
        },
      });
      if (data?.adminChangePhone) {
        await refetch();
      }
      return;
    }

    const { errors } = await changeUserPhone({
      input: { newPhone: values.phone },
    });
    if (!errors) {
      setCodeSent(true);
      enqueueSnackbar({
        message: formatMessage(messages.codeSent),
        variant: 'success',
      });
    }
  }

  async function handleVerify(
    values: FormValues,
    { resetForm }: { resetForm: () => void }
  ) {
    const { errors } = await verifyUserPhone({
      input: { confirmationCode: values.verificationCode.join('') },
    });
    if (!errors) {
      setCodeSent(false);
      if (onVerified) {
        onVerified(values.phone);
      } else {
        await refetch();
      }
      resetForm();
    }
  }

  return (
    <Grid2 container>
      <Grid2 size={{ xs: 12 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography color="primary">
            {formatMessage(messages.currentPhone)}
          </Typography>
          <Typography>
            {mfaResponse?.phone_number ?? formatMessage(messages.noPhone)}
          </Typography>
          {mfaResponse?.phone_number && (
            <Typography
              variant="caption"
              color={mfaResponse.phoneVerified ? 'success.main' : 'error.main'}
            >
              {formatMessage(
                mfaResponse.phoneVerified
                  ? messages.verified
                  : messages.notVerified
              )}
            </Typography>
          )}
        </Stack>
      </Grid2>
      <Grid2 size={{ xs: 12 }}>
        <Typography color="text.secondary" variant="body2">
          {formatMessage(messages.disclaimer)}
        </Typography>
      </Grid2>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={codeSent ? handleVerify : handleSubmitPhone}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, submitForm }) => (
          <div>
            {!codeSent ? (
              <>
                <FormikTelField
                  label={formatMessage(messages.newPhone)}
                  name="phone"
                  required
                />
                <RoundedButton disabled={isSubmitting} onClick={submitForm}>
                  {formatMessage(messages.submit)}
                </RoundedButton>
              </>
            ) : (
              <>
                <FormikCodeField
                  name="verificationCode"
                  codeLength={CODE_LENGTH}
                  autoSubmit={false}
                />
                <RoundedButton
                  disabled={isSubmitting || verifyLoading}
                  onClick={submitForm}
                >
                  {formatMessage(messages.verify)}
                </RoundedButton>
              </>
            )}
          </div>
        )}
      </Formik>
    </Grid2>
  );
}
